import React, { useEffect, useState } from 'react';
import { Alert, Button, Card, Descriptions, Space, Spin, Table, Typography } from 'antd';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { fetchOrderWarehouseSummary } from '../api/orderWarehouseApi';
import type { OrderWarehouseLine, OrderWarehouseSummary } from '../../../types/orderWarehouseSummary';
import { formatUnit } from '../../../utils/units';
import { formatCurrency } from '../../../utils/currency';

interface Props {
  orderId: string;
  canViewMargin: boolean;
  refreshKey?: unknown;
}

export default function OrderWarehouseSection({ orderId, canViewMargin, refreshKey }: Props) {
  const { t, i18n } = useTranslation(['orders', 'common', 'wz']);
  const [revision, setRevision] = useState(0);
  const [state, setState] = useState<{ orderId: string; summary?: OrderWarehouseSummary; failed?: boolean }>();
  useEffect(() => {
    let active = true;
    setState(undefined);
    fetchOrderWarehouseSummary(orderId).then(
      summary => { if (active) setState({ orderId, summary }); },
      () => { if (active) setState({ orderId, failed: true }); },
    );
    return () => { active = false; };
  }, [orderId, revision, refreshKey]);
  const summary = state?.orderId === orderId ? state.summary : undefined;
  const failed = state?.orderId === orderId && state.failed;
  const quantity = (value: number | null, line: OrderWarehouseLine) => value === null
    ? t('orders:warehouseFlow.unknownAvailability')
    : `${value.toLocaleString(i18n.language, { maximumFractionDigits: 4 })} ${formatUnit(line.unit, i18n.language)}`;
  return <Card title={t('orders:warehouseFlow.fulfillment')} size="small" style={{ marginBottom: 16 }}>
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Typography.Text type="secondary">{t('orders:warehouseFlow.fulfillmentHelp')}</Typography.Text>
      {failed ? <Alert type="warning" showIcon message={t('orders:warehouseFlow.loadError')}
        action={<Button onClick={() => setRevision(value => value + 1)}>{t('common:retry')}</Button>} />
        : !summary ? <Spin aria-label={t('common:messages.loading')} /> : <>
          <Table<OrderWarehouseLine> dataSource={summary.lines} rowKey="id" size="small" pagination={false}
            scroll={{ x: 650 }} columns={[
              { title: t('orders:warehouseFlow.product'), dataIndex: 'name', key: 'name' },
              ...(['orderedQuantity', 'availableQuantity', 'issuedQuantity', 'remainingQuantity', 'invoicedQuantity'] as const).map(key => ({
                title: t(`orders:warehouseFlow.${key}`), key,
                render: (_: unknown, line: OrderWarehouseLine) => quantity(line[key], line),
              })),
            ]} />
          {summary.documents.length > 0 && <Space wrap>
            {summary.documents.map(document => <Link key={document.id} to={`/app/wz/${encodeURIComponent(document.id)}`}>
              {document.wzNumber}
            </Link>)}
          </Space>}
          {canViewMargin && summary.finance && <>
            <Descriptions size="small" column={{ xs: 1, sm: 3 }}>
              <Descriptions.Item label={t('orders:warehouseFlow.fifoCost')}>
                {formatCurrency(summary.finance.knownFifoCostPLN, 'PLN')}
              </Descriptions.Item>
              <Descriptions.Item label={t('orders:warehouseFlow.transportCost')}>
                {formatCurrency(summary.finance.transportCostPLN, 'PLN')}
              </Descriptions.Item>
              <Descriptions.Item label={t('orders:warehouseFlow.otherCost')}>
                {formatCurrency(summary.finance.otherCostPLN, 'PLN')}
              </Descriptions.Item>
            </Descriptions>
            {summary.finance.costPending && <Alert type="warning" showIcon message={t('orders:warehouseFlow.costPending')} />}
          </>}
        </>}
      <Link to={`/app/wz/new?orderId=${encodeURIComponent(orderId)}`}>
        <Button type="primary">{t('orders:warehouseFlow.issueGoods')}</Button>
      </Link>
    </Space>
  </Card>;
}
